import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import { Typography, Space, Form, Switch } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getTranslatedString } from 'helpers/i18n';
import { ApplicationUIContext, MoneyInput } from '@yuhuinc/ui-library';
import { formatMoney } from 'helpers/Formatter';

const { Title } = Typography;
const StyledSpace = styled(Space)`
  width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const InlineFormItem = styled(Form.Item)`
  flex-direction: row-reverse !important;
  align-items: baseline;
  gap: 8px;
  width: fit-content;
`;

const rules = [
  {
    required: true,
    message: getTranslatedString(
      'landlordPortal.applicationManagement.inviteLeadToApply.requiredField'
    ),
  },
];

const RentDeposits = ({ disabled = false, selectedUnit }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const form = Form.useFormInstance();

  const marketRent = selectedUnit?.market_rent;

  const monthlyRentRules = [
    ...rules,
    {
      warningOnly: true,
      validator: (_, value) => {
        const currentValueInInput = value;

        if (
          typeof marketRent === 'number' &&
          typeof currentValueInInput === 'number' &&
          currentValueInInput < marketRent
        ) {
          return Promise.reject();
        }

        return Promise.resolve();
      },
      message: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent.monthlyRent.warning',
        {
          marketRent: formatMoney(marketRent),
        }
      ),
    },
    {
      validator: (_, value) => {
        const isFieldEmpty = value === undefined || value === null || value === '';
        if (isFieldEmpty) {
          form.setFieldsValue({ monthly_rent: marketRent });
        }

        return Promise.resolve();
      },
    },
  ];

  const rentDepositsTitle = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent'
  );
  const monthlyRent = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent.monthlyRent'
  );
  const rentalDeposit = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent.rentalDeposit'
  );
  const keyDeposit = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent.keyDeposit'
  );
  const earlyMoveInProration = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent.earlyMoveInDateProration'
  );
  const requiredForApplication = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent.requiredForApplication'
  );

  return (
    <StyledSpace direction="vertical" size="middle">
      <Title level={isMobile ? 4 : 5} style={{ margin: 0 }}>
        {rentDepositsTitle}
      </Title>

      <StyledRow isMobile={isMobile}>
        <Form.Item
          label={monthlyRent}
          name="monthly_rent"
          rules={monthlyRentRules}
          status="warning"
        >
          <MoneyInput disabled={disabled} />
        </Form.Item>

        <Form.Item label={rentalDeposit} name="deposit_amount" rules={rules}>
          <MoneyInput disabled={disabled} />
        </Form.Item>
      </StyledRow>

      <StyledRow isMobile={isMobile}>
        <Form.Item label={keyDeposit} name="key_deposit" rules={rules}>
          <MoneyInput disabled={disabled} />
        </Form.Item>

        <Form.Item
          label={earlyMoveInProration}
          name="prorated_rent_value"
          tooltip={{
            title: getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.rent.earlyMoveInDateProration.tooltip'
            ),
            icon: <InfoCircleOutlined />,
          }}
          rules={rules}
        >
          <MoneyInput disabled={true} />
        </Form.Item>
      </StyledRow>

      <InlineFormItem
        label={requiredForApplication}
        name="deposit_required_for_lease"
        valuePropName="checked"
      >
        <Switch size={!isMobile && 'small'} />
      </InlineFormItem>
    </StyledSpace>
  );
};

RentDeposits.propTypes = {
  disabled: PropTypes.bool,
  selectedUnit: PropTypes.object,
};

export default RentDeposits;
